import { useState } from 'react';
import { Banner, Button } from '@acs/ui';
import type { PlanTier } from '@acs/shared';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useI18n } from '../../i18n/index.js';
import { friendlyError } from '../../shared/api/errors.js';
import { useStartCheckout } from './hooks.js';
import { PLAN_CARDS, formatPlanPrice } from './plans.js';

/**
 * The monthly-allowance plans for a LEGACY business (doc 23) — one whose
 * currency the prepaid offer does not cover, or that went live before it.
 * Shown where the owner needs it: the wizard's Activate step and Settings ›
 * Billing. Checkout returns to whichever of the two applies
 * (`createCheckoutSession`).
 */
export function LegacyPlanPicker({ business, canceled }: { business: BusinessDoc; canceled?: boolean }) {
  const { t, tag } = useI18n();
  const checkout = useStartCheckout(business.id);
  const [chosen, setChosen] = useState<PlanTier | null>(null);

  return (
    <div className="subscribe">
      {/* Not an error: they pressed back in Stripe. Say so plainly and leave
          the plans where they were, rather than shouting about a failure. */}
      {canceled && <Banner tone="info">{t('billing.subscribe.canceled')}</Banner>}
      {checkout.isError && (
        <Banner tone="danger" title={t('billing.subscribe.failed')}>
          {friendlyError(t, checkout.error)}
        </Banner>
      )}

      <ul className="subscribe__plans">
        {PLAN_CARDS.map((plan) => (
          <li key={plan.tier} className={`subscribe__plan${plan.featured ? ' subscribe__plan--featured' : ''}`}>
            {plan.featured && <span className="subscribe__ribbon">{t('billing.plan.popular')}</span>}
            <h2 className="subscribe__plan-name">{t(plan.nameKey)}</h2>
            <p className="subscribe__price">
              {formatPlanPrice(plan.amount[business.currency], business.currency, tag)}
              <span className="subscribe__per">{t('billing.perMonth')}</span>
            </p>
            {/* The allowance is what separates the tiers, so it sits directly
                under the price. The product hard-caps rather than billing
                overage (doc 26 §1): "pauses", never a per-minute price. */}
            <p className="subscribe__allowance">
              {t('billing.plan.minutes', { count: plan.includedMinutes })}
              <span className="subscribe__overage">{t('billing.plan.overage')}</span>
            </p>
            <p className="subscribe__plan-desc">{t(plan.descKey)}</p>
            <Button
              size="lg"
              block
              variant={plan.featured ? 'primary' : 'secondary'}
              loading={checkout.isPending && chosen === plan.tier}
              // One payment at a time: a second click while Stripe is being
              // asked for a session would open two subscriptions.
              disabled={checkout.isPending}
              onClick={() => {
                setChosen(plan.tier);
                checkout.mutate({ plan: plan.tier });
              }}
            >
              {t('billing.subscribe.choose')}
            </Button>
          </li>
        ))}
      </ul>

      <p className="subscribe__foot">{t('billing.subscribe.foot')}</p>
    </div>
  );
}
